import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Rating,
    TextField,
    useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, StarRate as StarRateIcon } from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import type { ReviewModalProps } from './IReviewModal';
import { styles } from './styles';

const ReviewModal = ({
    open,
    jobID,
    repairerCompany,
    onClose,
    onSubmit,
}: ReviewModalProps) => {
    const theme = useTheme();
    const [rating, setRating] = useState<number | null>(0);
    const [note, setNote] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setRating(0);
            setNote('');
            setSubmitted(false);
        }, 200);
    };

    const handleSubmit = () => {
        if (!rating || !repairerCompany) return;
        setSubmitted(true);
    };

    const handleConfirm = () => {
        if (!rating || !repairerCompany) return;
        onSubmit({ rating, note, jobID, repairerCompany });
        handleClose();
    };

    const canSubmit = Boolean(rating && rating > 0);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={styles.title}>
                {submitted ? 'Success!' : 'Rate this vendor'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={styles.subtitle}>
                    {submitted
                        ? 'We value your feedback'
                        : 'Based on your experience with this vendor, please rate them with 1 star being terrible and 5 stars being excellent'}
                </Typography>
                {submitted ? (
                    <Box sx={styles.formStack}>
                        <CheckCircleIcon sx={{ color: theme.palette.blue[500], fontSize: 64 }} />
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography>{repairerCompany?.name}</Typography>
                            <Typography>{rating}</Typography>
                            <StarRateIcon sx={{ color: theme.palette.yellow[500], fontSize: 20 }} />
                        </Box>
                        <Button
                            variant="contained"
                            sx={styles.submitBtn(true, theme)}
                            onClick={handleConfirm}
                        >
                            Close
                        </Button>
                    </Box>
                ) : (
                    <Box sx={styles.formStack}>
                        <Rating
                            value={rating}
                            onChange={(_, value) => setRating(value)}
                            size="large"
                            sx={styles.rating}
                        />
                        <TextField
                            label="Leave a note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            variant="outlined"
                            size="small"
                            sx={styles.noteField}
                        />
                        <Button
                            variant="contained"
                            sx={styles.submitBtn(canSubmit, theme)}
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                        >
                            Submit
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReviewModal;
